import {Amplify, Auth} from 'aws-amplify';
import {clearPoolAndChatHistory} from './storage';

/**
 * Cognito configuration interface
 */
export interface CognitoConfig {
  region: string;
  userPoolId: string;
  userPoolWebClientId: string;
  domain: string;
  redirectSignIn: string;
  redirectSignOut: string;
  responseType?: string;
}

/**
 * User interface
 */
export interface User {
  username: string;
  attributes?: {
    email?: string;
    name?: string;
    [key: string]: any;
  };
  signInUserSession?: {
    idToken: {
      jwtToken: string;
      payload: any;
    };
    accessToken: {
      jwtToken: string;
      payload: any;
    };
    refreshToken?: {
      token: string;
    };
  };
  formattedAttributes?: UserAttributes;
}

/**
 * API Gateway configuration
 */
interface APIGatewayConfig {
  endpoints: Array<{
    name: string;
    endpoint: string;
  }>;
}

/**
 * Refreshes the current user session by retrieving its refreshToken
 * @param user - The authenticated user object
 * @returns Promise<void>
 */
export const refreshSession = async (user: any): Promise<void> => {
  if (!user) {
    throw new Error('No user provided for session refresh');
  }

  try {
    const currentSession = await Auth.currentSession();

    if (!currentSession.getIdToken()) {
      throw new Error('No current user session set');
    }

    const cognitoUser = await Auth.currentAuthenticatedUser();
    const refreshToken = currentSession.getRefreshToken();

    await new Promise((resolve, reject) =>
        cognitoUser.refreshSession(refreshToken, (err: any, session: any) => {
          if (err) {
            reject(err);
          } else {
            resolve(session);
          }
        })
    );

    console.info('User Session refreshed, expires on:',
        new Date((await Auth.currentSession()).getIdToken().getExpiration() * 1000)
    );
  } catch (error) {
    console.error('Error while attempting to refresh user session:', error);
    throw error;
  }
};

/**
 * Get domain path from location for state preservation
 * @param location - Window location object
 * @returns The path to preserve for post-login redirect
 */
const getDomainPathFromLocation = (location: Location): string => {
  return location.pathname + location.search + location.hash;
};

/**
 * Initiate federated sign-in with Amazon Federate
 * @returns Promise<User | undefined> - The authenticated user or undefined
 */
export const signInRedirect = async (): Promise<User | undefined> => {
  const requestedPath = getDomainPathFromLocation(window.location);

  try {
    await Auth.federatedSignIn({
      customProvider: 'AmazonFederate',
      customState: requestedPath
    });

    const user = await Auth.currentAuthenticatedUser();
    return user;
  } catch (error) {
    console.error('Error during federated sign-in:', error);
    throw error;
  }
};

/**
 * Setup Amplify with Cognito configuration
 * @param cognitoConfig - Cognito configuration object
 * @param apiConfig - Optional API Gateway configuration
 */
export const setupAmplify = async (
    cognitoConfig: CognitoConfig,
    apiConfig?: APIGatewayConfig
): Promise<void> => {
  const config: any = {
    Auth: {
      region: cognitoConfig.region,
      userPoolId: cognitoConfig.userPoolId,
      userPoolWebClientId: cognitoConfig.userPoolWebClientId,
      oauth: {
        domain: cognitoConfig.domain,
        scope: ['openid', 'email', 'profile'],
        redirectSignIn: cognitoConfig.redirectSignIn,
        redirectSignOut: cognitoConfig.redirectSignOut,
        responseType: cognitoConfig.responseType || 'code',
        // Use default browser redirect (required for OAuth code flow)
        urlOpener: (url: string) => {
          window.location.assign(url);
        },
      }
    }
  };

  // Add API configuration if provided
  if (apiConfig) {
    config.API = apiConfig;
  }

  console.log('Configuring Amplify with:', {
    region: cognitoConfig.region,
    userPoolId: cognitoConfig.userPoolId,
    domain: cognitoConfig.domain,
    redirectSignIn: cognitoConfig.redirectSignIn,
  });

  Amplify.configure(config);
};

/**
 * Authenticate user - main authentication entry point
 * Checks for existing session or initiates new sign-in
 * @param cognitoConfig - Cognito configuration
 * @param apiConfig - Optional API Gateway configuration
 * @returns Promise<User> - The authenticated user with formatted attributes
 */
export const authenticateUser = async (
    cognitoConfig: CognitoConfig,
    apiConfig?: APIGatewayConfig
): Promise<User> => {
  try {
    // Setup Amplify with provided configuration
    await setupAmplify(cognitoConfig, apiConfig);

    // Check if user is already authenticated
    const user = await Auth.currentAuthenticatedUser();

    // Add formatted attributes
    if (user) {
      user.formattedAttributes = buildUserAttributes(user);
    }

    return user;
  } catch (error) {

    // If not authenticated, initiate federated sign-in
    // This will redirect the browser, so it won't return
    try {
      await signInRedirect();
    } catch (redirectError) {
      // Redirect errors are expected (the page is navigating away)
      console.log('Redirecting to authentication provider...');
    }

    // Throw a specific error that indicates we're redirecting
    // This helps the calling code know this is expected behavior
    const redirectError = new Error('REDIRECT_IN_PROGRESS');
    (redirectError as any).code = 'REDIRECT_IN_PROGRESS';
    throw redirectError;
  }
};

/**
 * Sign out the current user
 * @returns Promise<any> - Sign out result
 */
export const signOut = async (): Promise<any> => {
  try {
    // Clear local storage
    clearPoolAndChatHistory();

    // Sign out from Amplify
    return await Auth.signOut();
  } catch (error) {
    console.error('Error while signing out:', error);
    // Return empty string to maintain compatibility
    return '';
  }
};

/**
 * Get current session
 * @returns Promise<any> - The current session object
 */
export const getCurrentSession = async (): Promise<any> => {
  try {
    const session = await Auth.currentSession();
    return session;
  } catch (error) {
    console.error('Error getting current session:', error);
    throw error;
  }
};

/**
 * Get current authenticated user
 * @returns Promise<User> - The current authenticated user with formatted attributes
 */
export const getCurrentAuthenticatedUser = async (): Promise<User> => {
  try {
    const user = await Auth.currentAuthenticatedUser();

    // Add formatted attributes
    if (user) {
      user.formattedAttributes = buildUserAttributes(user);
    }

    return user;
  } catch (error) {
    console.error('Error getting current authenticated user:', error);
    throw error;
  }
};

/**
 * Get the id token, access token and username from the current session
 * @returns Promise<[string, string, string]> - Array of [idToken, accessToken, username]
 */
export const getCurrentUserIdentifiers = async (): Promise<[string, string, string]> => {
  const authenticatedUser = await Auth.currentAuthenticatedUser();

  return [
    authenticatedUser.signInUserSession.idToken.jwtToken,
    authenticatedUser.signInUserSession.accessToken.jwtToken,
    authenticatedUser.username?.replace('AmazonFederate_', '').toLowerCase()
  ];
};

/**
 * Get the user name from the current session
 * Removes the 'AmazonFederate_' prefix if present
 * @returns Promise<string> - The username
 */
export const getCurrentUserName = async (): Promise<string> => {
  const authenticatedUser = await Auth.currentAuthenticatedUser();
  const username = authenticatedUser.username;

  // Remove federate prefix if present
  return username.startsWith('AmazonFederate_')
      ? username.substring(15)
      : username;
};

/**
 * Check if user is authenticated
 * @returns Promise<boolean> - True if authenticated, false otherwise
 */
export const isAuthenticated = async (): Promise<boolean> => {
  try {
    await Auth.currentAuthenticatedUser();
    return true;
  } catch {
    return false;
  }
};

/**
 * Get user attributes from the current session
 * @returns Promise<any> - User attributes object
 */
export const getUserAttributes = async (): Promise<any> => {
  try {
    const user = await Auth.currentAuthenticatedUser();
    const attributes = await Auth.userAttributes(user);

    // Convert array to object for easier access
    const attributesObject: any = {};
    attributes.forEach((attr: any) => {
      attributesObject[attr.Name] = attr.Value;
    });

    return attributesObject;
  } catch (error) {
    console.error('Error getting user attributes:', error);
    throw error;
  }
};

/**
 * Format username by removing federate prefix
 * @param userName - Raw username from Cognito
 * @returns Formatted username
 */
export const formatUserName = (userName: string): string => {
  return userName.replace('AmazonFederate_', '').toLowerCase();
};

/**
 * Get PhoneTool avatar URL for internal Amazon users
 * @param userName - Username (will be formatted)
 * @returns Avatar URL
 */
export const getPhoneToolAvatarUrl = (userName: string): string => {
  const formattedUserName = formatUserName(userName);
  return `https://internal-cdn.amazon.com/badgephotos.amazon.com/?uid=${formattedUserName}`;
};

/**
 * User attributes interface
 */
export interface UserAttributes {
  username: string;
  givenName?: string;
  email?: string;
  avatarUrl?: string;
}

/**
 * Build formatted user attributes from Cognito user
 * @param user - Cognito user object
 * @returns Formatted user attributes
 */
export const buildUserAttributes = (user: any): UserAttributes => {
  const cognitoPayload = user.signInUserSession?.idToken?.payload;

  return {
    username: formatUserName(user.username),
    givenName: cognitoPayload?.given_name || cognitoPayload?.name,
    email: cognitoPayload?.email,
    avatarUrl: getPhoneToolAvatarUrl(user.username)
  };
};
